import AsyncStorage from "@react-native-async-storage/async-storage";
import React, { useCallback, useEffect, useState } from "react";
import { Text, TouchableOpacity, View } from "react-native";
import { Icon } from "react-native-elements";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { t } from "../services/i18n";
import {
	isWebPushAvailable,
	registerWebPush
} from "../services/WebPushRegistration";
import { COLOR2, DARKGREY_COLOR, WHITE_COLOR } from "../styles/commonStyles";
import { getPermission, PERMISSION_DEFAULT } from "../utils/WebPush";

const STORAGE_KEY = "notifications_prompt_dismissed";

/**
 * Asks for the notification permission from a tap.
 *
 * WebKit — Safari on macOS and every browser on iOS — only honours
 * `Notification.requestPermission()` inside a user gesture, so the automatic
 * registration that runs after login cannot prompt there. This banner is the
 * gesture. It stays out of the way of `CompactInstallPrompt`: on iOS it needs
 * the PWA to already be installed (`isWebPushAvailable`), which is exactly when
 * the install prompt stops showing.
 */
const EnableNotificationsPrompt = () => {
	const dispatch = useDispatch();
	const [visible, setVisible] = useState(false);
	const { user, lang } = useSelector(
		state => ({
			user: state.profileReducer.user,
			lang: state.globalInformationsReducer.lang
		}),
		shallowEqual
	);

	const refresh = useCallback(async () => {
		if (!user || !isWebPushAvailable()) {
			setVisible(false);
			return;
		}
		// `denied` is a dead end until the user changes it in the browser
		// settings, and `granted` means the automatic path already has a token.
		if (getPermission() !== PERMISSION_DEFAULT) {
			setVisible(false);
			return;
		}
		try {
			const dismissed = await AsyncStorage.getItem(STORAGE_KEY);
			setVisible(dismissed !== "true");
		} catch (error) {
			setVisible(true);
		}
	}, [user]);

	useEffect(() => {
		refresh();
	}, [refresh]);

	const enable = async () => {
		// `registerWebPush` calls `Notification.requestPermission()` first and
		// synchronously, which is what keeps this inside the gesture on WebKit.
		await registerWebPush(dispatch, lang, { promptIfNeeded: true });
		setVisible(false);
	};

	const dismiss = async () => {
		setVisible(false);
		try {
			await AsyncStorage.setItem(STORAGE_KEY, "true");
		} catch (error) {
			// The banner simply comes back on the next launch.
		}
	};

	if (!visible) {
		return null;
	}

	return (
		<View style={styles.container}>
			<View style={styles.content}>
				<View style={styles.iconContainer}>
					<Icon
						name="bell-outline"
						type="material-community"
						size={20}
						color={COLOR2}
					/>
				</View>

				<View style={styles.textContainer}>
					<Text style={styles.title}>{t("notifications:enableTitle")}</Text>
					<Text style={styles.description}>
						{t("notifications:enableHint")}
					</Text>
				</View>

				<View style={styles.buttonContainer}>
					<TouchableOpacity onPress={enable} style={styles.enableButton}>
						<Text style={styles.enableButtonText}>
							{t("notifications:enableButton")}
						</Text>
					</TouchableOpacity>

					<TouchableOpacity onPress={dismiss} style={styles.dismissButton}>
						<Icon
							name="close"
							type="material-community"
							size={16}
							color={DARKGREY_COLOR}
						/>
					</TouchableOpacity>
				</View>
			</View>
		</View>
	);
};

const styles = {
	container: {
		position: "fixed",
		top: 0,
		left: 0,
		right: 0,
		backgroundColor: WHITE_COLOR,
		borderBottomWidth: 1,
		borderBottomColor: "#e0e0e0",
		paddingHorizontal: 12,
		paddingVertical: 8,
		zIndex: 1000,
		boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
	},
	content: {
		flexDirection: "row",
		alignItems: "center",
		width: "100%"
	},
	iconContainer: {
		marginRight: 8
	},
	textContainer: {
		flex: 1,
		marginRight: 8
	},
	title: {
		fontSize: 14,
		fontWeight: "bold",
		color: "#333",
		marginBottom: 2
	},
	description: {
		fontSize: 12,
		color: DARKGREY_COLOR,
		lineHeight: 16
	},
	buttonContainer: {
		flexDirection: "row",
		alignItems: "center"
	},
	enableButton: {
		backgroundColor: COLOR2,
		borderRadius: 6,
		paddingHorizontal: 12,
		paddingVertical: 6,
		marginRight: 8
	},
	enableButtonText: {
		fontSize: 12,
		fontWeight: "600",
		color: WHITE_COLOR
	},
	dismissButton: {
		padding: 4
	}
};

export default EnableNotificationsPrompt;
